import { useEffect, useMemo, useState } from "react";
import { glyphs } from "../../../../../Styles";
import { NewButton, GenericDeviceTable, ResetTableModal, ConfirmModal, ResetTableClearQueueModal } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, LC_TABLE_IDS } from "../../../../../Definitions";
import { LC_FEED_TIME, LCFeedTimeArgs, LCFeedTimeResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcFeedTime";
import { RESET_FEEDING_TIMES, ResetFeedingTimesArgs, ResetFeedingTimesResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/resetFeedingTimes";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "milestoneId", accessor: "milestoneId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feedcurveId", accessor: "feedcurveId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "trigger", accessor: "trigger", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "state", accessor: "state", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "triggerValue", accessor: "triggerValue", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "day", accessor: "day", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "nday", accessor: "nday", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "amount", accessor: "amount", type: ListData_ValueTypes.Number, sort: true },
]

export default function FeedTimeDataTable() {
	const [isResetTableModalOpen, setIsResetTableModalOpen] = useState<boolean>(false);
	const [isResetTableClearQueueModalOpen, setIsResetTableClearQueueModalOpen] = useState<boolean>(false);

	const { state: connectionState } = useAicoConnection();

	const [fetchData, { data, loading, error }] = useLazyQuery<LCFeedTimeResponse, LCFeedTimeArgs>(LC_FEED_TIME, { fetchPolicy: "network-only" });

	useEffect(() => {
		if (connectionState.deviceConnection) {
			fetchData({ variables: connectionState.deviceConnection });
		}
	}, [connectionState.deviceConnection]);

	const tableData = useMemo(() => {
		return data?.controller.lcFeedTime?.map((e, i) => ({
			...e,
			id: i
		})) ?? [];
	}, [data]);

	function handleOnSuccess() {
		if (connectionState.deviceConnection) {
			fetchData({ variables: connectionState.deviceConnection });
		}
	}

	// #region | Reset Feed Times Modal
	const [isResetFeedTimesModalOpen, setIsResetFeedTimesModalOpen] = useState<boolean>(false);

	const [resetFetch, { data: resetData, loading: resetLoading, error: resetError, reset: resetResetState }] = useMutation<ResetFeedingTimesResponse, ResetFeedingTimesArgs>(RESET_FEEDING_TIMES, {
		fetchPolicy: "network-only"
	});

	async function handleResetFeedTimes() {
		if (!connectionState.deviceConnection) {
			return;
		}

		const result = await resetFetch({ variables: connectionState.deviceConnection });
		if (result.data?.controller.resetFeedingTimes?.success) {
			handleOnSuccess();
		}
	}
	// #endregion

	return ( <>
		<GenericDeviceTable
			title="Feed Times"
			columnsData={columnsData}
			data={tableData}
			loading={loading}
			error={error}
			onFetch={() => connectionState.deviceConnection && fetchData({ variables: connectionState.deviceConnection })}
			tabButtons={[
				<NewButton
					content="Reset Times"
					startEnhancer={glyphs.ArrowReset({})}
					onClick={() => {
						setIsResetFeedTimesModalOpen(true);
					}}
					buttonType={ButtonTypes.tableButton}
					overrides={{
						contentStyles: {whiteSpace: "nowrap"}
					}}
				/>,
				<NewButton
					key="Reset table"
					content="Reset Table"
					startEnhancer={glyphs.ArrowReset({})}
					onClick={() => {
						setIsResetTableModalOpen(true);
					}}
					overrides={{
						contentStyles: {whiteSpace: "nowrap"}
					}}
					buttonType={ButtonTypes.tableButton}
					disabled={!connectionState.deviceConnection}
				/>,
				<NewButton
					key="Reset & clear"
					content="Reset & Clear"
					startEnhancer={glyphs.ArrowReset({})}
					onClick={() => {
						setIsResetTableClearQueueModalOpen(true);
					}}
					buttonType={ButtonTypes.tableButton}
					disabled={!connectionState.deviceConnection}
					overrides={{
						contentStyles: { minWidth: "95px" }
					}}
				/>
			]}
		/>
		{connectionState.deviceConnection && isResetTableModalOpen &&
			<ResetTableModal
				closeModal={() => { setIsResetTableModalOpen(false) }}
			onSuccess={() => { handleOnSuccess(); setIsResetTableModalOpen(false) }}
				tableId={LC_TABLE_IDS.feedplanTimes}
			/>
		}
		{connectionState.deviceConnection && isResetFeedTimesModalOpen &&
			<ConfirmModal
				loading={resetLoading}
				disclaimer="This action will reset the feed times on the LAN Controller, this action is irreversible!"
				onCancel={() => {resetResetState(); setIsResetFeedTimesModalOpen(false)}}
				onConfirm={() => { handleResetFeedTimes() }}
				message={
					(resetError || (resetData?.controller && !resetData.controller.resetFeedingTimes?.success)) ? "ERROR: Failed To Reset Feed Times" :
					(!resetLoading && !resetError && resetData?.controller.resetFeedingTimes?.success) ? "Succesfully Reset Feed Times" : undefined
					}
			/>
		}
		{connectionState.deviceConnection && isResetTableClearQueueModalOpen &&
			<ResetTableClearQueueModal
				closeModal={()=>{ setIsResetTableClearQueueModalOpen(false) }}
				onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
				tableId={LC_TABLE_IDS.feedplanTimes}
			/>
		}
	</> )
}